import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandLogo, BrandLockup } from "@/components/BrandLogo";
import RippleAccent from "@/components/media/RippleAccent";
import { BRAND } from "@/lib/brand";
import { startGoogleLogin } from "@/context/AuthContext";

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.5 29.6 2.5 24 2.5 12.1 2.5 2.5 12.1 2.5 24S12.1 45.5 24 45.5 45.5 35.9 45.5 24c0-1.2-.1-2.3-.3-3.5z"/>
      <path fill="#FF3D00" d="M6 14.7l6.6 4.8C14.3 15.1 18.8 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6 29.6 4 24 4 15.9 4 8.9 8.6 6 14.7z" transform="translate(0 -1.5)"/>
      <path fill="#4CAF50" d="M24 45.5c5.5 0 10.4-1.9 14.3-5.1l-6.6-5.6c-2 1.5-4.6 2.4-7.7 2.4-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C8.8 40.7 15.8 45.5 24 45.5z"/>
      <path fill="#1976D2" d="M43.6 20.5H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.6 5.6C41.6 36.9 45.5 31.1 45.5 24c0-1.2-.1-2.3-.3-3.5z"/>
    </svg>
  );
}

export function AuthShell({ children }) {
  return (
    <div className="min-h-screen bg-ivory grid lg:grid-cols-12">
      <aside className="relative hidden lg:flex lg:col-span-5 bg-forest text-ivory flex-col justify-between p-12 overflow-hidden grain">
        <RippleAccent className="absolute -right-56 -top-40 w-[760px] h-[760px] opacity-70" testid="auth-ripple-accent" />
        <BrandLogo
          variant="mark"
          tone="light"
          className="pointer-events-none select-none absolute -left-24 -bottom-32 w-[560px] opacity-[0.06]"
        />
        <Link to="/" className="relative inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ivory/60 hover:text-ivory transition-colors">
          <ArrowLeft size={14} /> Back to site
        </Link>
        <div className="relative">
          <BrandLogo tone="light" className="h-40 w-auto" />
          <p className="mt-10 font-serif font-light text-4xl leading-tight text-balance max-w-sm">
            {BRAND.heroCopy}
          </p>
          <p className="mt-6 font-serif italic text-2xl text-champagne">{BRAND.tagline}</p>
        </div>
        <p className="relative text-[11px] uppercase tracking-[0.22em] text-ivory/45">{BRAND.participation}</p>
      </aside>

      <div className="lg:col-span-7 flex flex-col">
        <header className="lg:hidden border-b border-forest/10">
          <div className="px-5 h-20 flex items-center justify-between">
            <Link to="/" className="inline-flex items-center"><BrandLockup compact /></Link>
            <Link to="/" className="text-[11px] uppercase tracking-[0.2em] text-charcoal/60 hover:text-forest">Back</Link>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-5 py-14 sm:py-20">{children}</div>
      </div>
    </div>
  );
}

export function GoogleButton({ next = "/my-rituals", label = "Continue with Google", testid = "google-signin-button" }) {
  return (
    <button
      onClick={() => startGoogleLogin(next)}
      data-testid={testid}
      className="w-full inline-flex items-center justify-center gap-3 bg-cream border border-forest/25 px-6 py-4 text-[12px] uppercase tracking-[0.18em] font-medium text-charcoal hover:border-forest hover:shadow-[0_12px_30px_-18px_rgba(42,73,44,0.5)] transition-all duration-300"
    >
      <GoogleMark /> {label}
    </button>
  );
}
